import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { errorMessage } from "@/lib/api";
import { useTenantConfig } from "@/hooks/useTenantConfig";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plug, KeyRound, Mail, MessageSquare, Briefcase, BarChart3 } from "lucide-react";

/**
 * Integrations — step 6 of tenant setup. Connect external systems. Each
 * toggle persists into featureFlags.integrations via the real
 * /configuration PATCH. (Actual OAuth handshakes are a later backend
 * concern; this screen records which integrations the tenant has enabled,
 * which is what Talent/Hub read to decide what to surface.)
 */
const INTEGRATIONS = [
  { id: "sso", icon: KeyRound, name: "SSO / SAML", desc: "Single sign-on for your team via your IdP." },
  { id: "email", icon: Mail, name: "Email (SMTP / SES)", desc: "Send transactional and campaign email." },
  { id: "slack", icon: MessageSquare, name: "Slack", desc: "Notifications and alerts in your workspace." },
  { id: "jobBoards", icon: Briefcase, name: "Job Boards", desc: "Post openings to external job boards." },
  { id: "analytics", icon: BarChart3, name: "Analytics", desc: "Export events to your analytics stack." },
];

export default function AdminIntegrations() {
  const { config, loading, save, error: cfgError, setError } = useTenantConfig();
  const [integrations, setIntegrations] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (config) setIntegrations(config.featureFlags?.integrations || {});
  }, [config]);

  const toggle = (id, v) => setIntegrations((prev) => ({ ...prev, [id]: v }));

  const onSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await save({ featureFlags: { ...(config?.featureFlags || {}), integrations } });
      setSaved(true);
    } catch (err) {
      const m = errorMessage(err);
      if (m) setError(m);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout variant="tenant">
      <h1 className="font-heading text-2xl font-bold">Integrations</h1>
      <p className="mt-1 text-muted-foreground">Connect the tools your team already uses.</p>
      {cfgError && <p className="mt-6 text-sm text-destructive" data-testid="integrations-error">{cfgError}</p>}
      {loading && <Loader2 className="mt-8 h-6 w-6 animate-spin text-primary" />}

      {!loading && (
        <div className="mt-6 max-w-3xl space-y-6">
          <Card>
            <CardContent className="divide-y divide-border p-0" data-testid="integrations-list">
              {INTEGRATIONS.map(({ id, icon: Icon, name, desc }) => (
                <div key={id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent/50 text-primary"><Icon className="h-4 w-4" /></span>
                    <div>
                      <p className="text-sm font-medium">{name}</p>
                      <p className="text-xs text-muted-foreground">{desc}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{integrations[id] ? "Enabled" : "Off"}</span>
                    <Switch checked={!!integrations[id]} onCheckedChange={(v) => toggle(id, v)} data-testid={`integration-${id}`} aria-label={name} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
          <div className="flex items-center gap-3">
            <Button onClick={onSave} disabled={saving} data-testid="integrations-save-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes
            </Button>
            {saved && <p className="text-sm text-emerald-600" data-testid="integrations-saved">Saved.</p>}
            <span className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground"><Plug className="h-3.5 w-3.5" /> Connection setup coming soon</span>
          </div>
        </div>
      )}
    </Layout>
  );
}
